import { useState, useEffect, useMemo } from 'react';
import SharePointConnectCard from '../../components/SharePointIntake/SharePointConnectCard.jsx';

const API = process.env.REACT_APP_API_URL || '';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const DOMAIN_RE = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;

const SIGNIN_OPTIONS = [
  {
    value: 'local',
    label: 'PCG-managed accounts',
    blurb: 'The super user gets a welcome email with a set-password link. We hold their password and a TOTP MFA secret.',
  },
  {
    value: 'federated',
    label: 'Sign in with Microsoft (Entra ID)',
    blurb: 'The super user signs in with their corporate Microsoft account. We never see their password. Their tenant\'s MFA + Conditional Access apply.',
  },
  {
    value: 'hybrid',
    label: 'Both — Microsoft + PCG-managed',
    blurb: 'Useful when most staff are on Microsoft but a few contractors need passwords. Both sign-in paths stay open.',
  },
];

const JIT_ROLE_OPTIONS = [
  { value: 'associate', label: 'Associate' },
  { value: 'admin', label: 'Admin' },
  { value: 'superuser', label: 'Super User (org)' },
];

/**
 * Single-screen wizard to provision a new customer organization.
 *
 * Sections:
 *   1. Organization (name)
 *   2. Apps Purchased
 *   3. Sign-in method (PCG-managed | Microsoft | Hybrid) + federation config
 *   4. First Org Superadmin
 *
 * Federated path skips the activate-password email and sends the Microsoft
 * sign-in email instead — single welcome email, single sign-in path.
 */
export default function ProvisionOrgWizard({ onClose, onProvisioned }) {
  const [step, setStep] = useState(1); // 1 = form, 3 = submitting, 4 = post-provision SharePoint prompt
  const [error, setError] = useState(null);
  const [provisionResult, setProvisionResult] = useState(null);

  const [orgName, setOrgName] = useState('');
  // 'health_plan' (a standalone customer) or 'tpa' (a third-party
  // administrator that will add the health plans it serves as clients).
  const [accountKind, setAccountKind] = useState('health_plan');
  const [logoFile, setLogoFile] = useState(null);

  const [apps, setApps] = useState([]);
  const [selectedApps, setSelectedApps] = useState({});
  const [appsLoading, setAppsLoading] = useState(true);

  const [adminName, setAdminName] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [sendInvite, setSendInvite] = useState(true);
  const [initialPassword, setInitialPassword] = useState('');

  // Sign-in method
  const [signInMode, setSignInMode] = useState('local');
  const [tenantId, setTenantId] = useState('');
  const [domains, setDomains] = useState([]);
  const [domainInput, setDomainInput] = useState('');
  const [jitEnabled, setJitEnabled] = useState(false);
  const [jitRole, setJitRole] = useState('associate');

  // Auto-derive a default verified domain from the admin email — most setups
  // need exactly one domain (the company's own), so make that one keystroke.
  const adminDomain = useMemo(() => {
    const at = adminEmail.indexOf('@');
    return at >= 0 ? adminEmail.slice(at + 1).toLowerCase().trim() : '';
  }, [adminEmail]);
  useEffect(() => {
    if (signInMode === 'local') return;
    if (adminDomain && DOMAIN_RE.test(adminDomain) && domains.length === 0) {
      setDomains([adminDomain]);
    }
  }, [adminDomain, signInMode, domains.length]);

  // Load app catalog
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/admin/v2/catalog/apps`, { credentials: 'include' });
        const data = await res.json();
        if (data.success) {
          setApps(data.apps || []);
          const initial = {};
          for (const app of data.apps || []) {
            if (app.is_included_by_default) initial[app.name] = true;
          }
          setSelectedApps(initial);
        }
      } catch (err) {
        setError('Failed to load app catalog');
      }
      setAppsLoading(false);
    })();
  }, []);

  const toggleApp = (name) => {
    setSelectedApps(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const addDomain = () => {
    const d = domainInput.trim().toLowerCase();
    if (!d) return;
    if (!DOMAIN_RE.test(d)) {
      setError(`Invalid domain: ${d}`);
      return;
    }
    if (!domains.includes(d)) setDomains([...domains, d]);
    setDomainInput('');
    setError(null);
  };

  const removeDomain = (d) => setDomains(domains.filter((x) => x !== d));

  const validate = () => {
    if (!orgName.trim()) return 'Organization name is required';
    if (!adminName.trim()) return 'First Org Superadmin name is required';
    if (!adminEmail.trim()) return 'First Org Superadmin email is required';
    if (!/^\S+@\S+\.\S+$/.test(adminEmail)) return 'Email format is invalid';

    if (signInMode !== 'local') {
      if (!UUID_RE.test(tenantId.trim())) {
        return 'Microsoft Entra tenant ID must be a valid UUID';
      }
      if (domains.length === 0) {
        return 'At least one verified email domain is required for Microsoft sign-in';
      }
      if (adminDomain && !domains.includes(adminDomain)) {
        return `The super user's email domain (${adminDomain}) must be in the verified domains. Add it or use a different email.`;
      }
    } else {
      if (!sendInvite && (!initialPassword || initialPassword.length < 8)) {
        return 'When not sending an invite, you must set an initial password (8+ chars)';
      }
    }
    return null;
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }
    setError(null);
    setStep(3);

    const purchasedProductNames = Object.entries(selectedApps)
      .filter(([_, on]) => on)
      .map(([name]) => name);

    const payload = {
      name: orgName.trim(),
      account_kind: accountKind,
      product_names: purchasedProductNames,
      first_admin: {
        name: adminName.trim(),
        email: adminEmail.trim(),
        send_invite: signInMode === 'local' ? sendInvite : false,
        initial_password: signInMode === 'local' && !sendInvite ? initialPassword : null,
      },
    };

    if (signInMode !== 'local') {
      payload.federation = {
        identity_mode: signInMode,
        azure_tenant_id: tenantId.trim().toLowerCase(),
        allowed_email_domains: domains,
        jit_provisioning_enabled: jitEnabled,
        jit_default_role: jitEnabled ? jitRole : null,
      };
    }

    try {
      const res = await fetch(`${API}/api/admin/v2/orgs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        setProvisionResult(data);
        setStep(4); // Optional SharePoint connect step (skippable)
      } else {
        setError(data.message || 'Failed to provision organization');
        setStep(1);
      }
    } catch (err) {
      setError('Network error: ' + err.message);
      setStep(1);
    }
  };

  const finishWizard = () => {
    if (provisionResult) onProvisioned(provisionResult);
    else onClose();
  };

  // Group apps for display
  const includedApps = apps.filter(a => a.category === 'included');
  const premiumApps = apps.filter(a => a.category === 'premium');
  const addonApps = apps.filter(a => a.category === 'addon');

  const tenantValid = signInMode === 'local' || UUID_RE.test(tenantId.trim());

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="relative bg-slate-900 border border-slate-700/50 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-700/50 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-lg font-bold text-white">Provision New Organization</h2>
            <p className="text-xs text-slate-500 mt-0.5">Create a customer org and invite their first admin</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white text-2xl leading-none w-8 h-8 flex items-center justify-center">×</button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          {step === 3 ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 mx-auto mb-4 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
              <p className="text-slate-300">Provisioning {orgName}…</p>
            </div>
          ) : step === 4 ? (
            <div className="space-y-5">
              {/* Success header */}
              <div className="text-center pt-2 pb-4">
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-emerald-500/30 to-blue-500/30 border border-emerald-500/40 mb-3">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-emerald-300">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-white">{orgName} is ready</h3>
                <p className="text-xs text-slate-400 mt-1">
                  {signInMode === 'local'
                    ? `${adminName} will receive a welcome email shortly.`
                    : `${adminName} will receive a “Sign in with Microsoft” welcome email shortly.`}
                </p>
              </div>

              {/* Optional next step: connect SharePoint */}
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-full bg-blue-500/20 border border-blue-500/40 text-blue-400 text-xs font-bold flex items-center justify-center">
                    5
                  </div>
                  <h3 className="text-sm font-semibold text-white uppercase tracking-wide">Document Intake (Optional)</h3>
                </div>
                <SharePointConnectCard
                  orgId={provisionResult?.organization?.id || provisionResult?.org_id}
                  orgName={orgName}
                  inviterName={adminName}
                  defaultAdminEmail={adminEmail}
                  onSkip={finishWizard}
                  compact
                />
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Section 1: Organization */}
              <Section title="Organization" number="1">
                <Field label="Organization Name *">
                  <input
                    value={orgName}
                    onChange={e => setOrgName(e.target.value)}
                    placeholder="e.g. Acme Health Plan"
                    className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none transition"
                  />
                </Field>
                <Field label="Account type *">
                  <div className="grid gap-2 md:grid-cols-2">
                    {[
                      { value: 'health_plan', title: 'Health plan', desc: 'One organization, its own people and data.' },
                      { value: 'tpa', title: 'Third-party administrator', desc: 'Serves several health plans. Adds each plan as a separate client workspace and decides which staff work in which.' },
                    ].map((opt) => (
                      <label key={opt.value} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                        accountKind === opt.value ? 'border-blue-500 bg-blue-500/10' : 'border-slate-700 hover:border-slate-600'
                      }`}>
                        <input type="radio" name="account_kind" value={opt.value} checked={accountKind === opt.value}
                          onChange={() => setAccountKind(opt.value)} className="mt-1" />
                        <span>
                          <span className="block text-sm font-medium text-white">{opt.title}</span>
                          <span className="block text-xs text-slate-400">{opt.desc}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                </Field>
              </Section>

              {/* Section 2: Apps */}
              <Section title="Apps Purchased" number="2">
                {appsLoading ? (
                  <p className="text-slate-500 text-sm">Loading catalog…</p>
                ) : (
                  <>
                    {includedApps.length > 0 && (
                      <AppGroup label="Included (every org gets these)">
                        {includedApps.map(app => (
                          <AppCheckbox key={app.name} app={app} checked={selectedApps[app.name]} onToggle={() => toggleApp(app.name)} />
                        ))}
                      </AppGroup>
                    )}
                    {premiumApps.length > 0 && (
                      <AppGroup label="Premium / Add-ons">
                        {premiumApps.map(app => (
                          <AppCheckbox key={app.name} app={app} checked={selectedApps[app.name]} onToggle={() => toggleApp(app.name)} />
                        ))}
                      </AppGroup>
                    )}
                    {addonApps.length > 0 && (
                      <AppGroup label="Modules">
                        {addonApps.map(app => (
                          <AppCheckbox key={app.name} app={app} checked={selectedApps[app.name]} onToggle={() => toggleApp(app.name)} />
                        ))}
                      </AppGroup>
                    )}
                  </>
                )}
              </Section>

              {/* Section 3: Sign-in method */}
              <Section title="How will their team sign in?" number="3">
                <div className="space-y-2">
                  {SIGNIN_OPTIONS.map((opt) => (
                    <label key={opt.value} className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition ${
                      signInMode === opt.value
                        ? 'border-blue-500 bg-blue-500/10'
                        : 'border-slate-700 hover:border-slate-600 bg-slate-800/40'
                    }`}>
                      <input
                        type="radio"
                        name="signin_mode"
                        value={opt.value}
                        checked={signInMode === opt.value}
                        onChange={() => setSignInMode(opt.value)}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-medium text-white">{opt.label}</div>
                        <div className="text-xs text-slate-400 mt-0.5">{opt.blurb}</div>
                      </div>
                    </label>
                  ))}
                </div>

                {signInMode !== 'local' && (
                  <div className="mt-4 space-y-4 rounded-xl border border-slate-700 bg-slate-800/30 p-4">
                    <Field label="Microsoft Entra tenant ID *">
                      <input
                        type="text"
                        value={tenantId}
                        onChange={(e) => setTenantId(e.target.value)}
                        placeholder="00000000-0000-0000-0000-000000000000"
                        className={`w-full bg-slate-800 border rounded-lg px-4 py-2.5 text-white text-sm font-mono placeholder-slate-500 focus:outline-none ${
                          tenantValid ? 'border-slate-700 focus:border-blue-500' : 'border-red-500/60'
                        }`}
                      />
                      <p className="text-[11px] text-slate-500 mt-1">
                        Ask the client&apos;s IT admin for their tenant ID. They copy it from <span className="font-mono">portal.azure.com → Microsoft Entra ID → Overview → Tenant ID</span>.
                      </p>
                    </Field>

                    <Field label="Verified email domains *">
                      <div className="flex flex-wrap gap-2 mb-2">
                        {domains.map((d) => (
                          <span key={d} className="px-2.5 py-1 rounded-lg bg-slate-700/60 border border-slate-600 text-xs text-white inline-flex items-center gap-2">
                            {d}
                            <button onClick={() => removeDomain(d)} type="button" className="text-slate-400 hover:text-red-300 leading-none">×</button>
                          </span>
                        ))}
                        {domains.length === 0 && (
                          <span className="text-xs text-slate-500 italic">No domains yet — type one and press Enter</span>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={domainInput}
                          onChange={(e) => setDomainInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addDomain(); } }}
                          placeholder="clientco.com"
                          className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white text-sm placeholder-slate-500 focus:outline-none focus:border-blue-500"
                        />
                        <button
                          onClick={addDomain}
                          type="button"
                          className="px-3 py-2 rounded-lg bg-blue-500/20 border border-blue-500/40 text-blue-200 text-sm hover:bg-blue-500/30"
                        >
                          Add
                        </button>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-1">
                        Only emails ending with one of these are accepted at sign-in.
                      </p>
                    </Field>

                    <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-3">
                      <label className="flex items-start gap-3 cursor-pointer">
                        <input type="checkbox" checked={jitEnabled} onChange={(e) => setJitEnabled(e.target.checked)} className="mt-1" />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-white">Auto-create users on first sign-in</div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            If on, anyone from a verified domain can sign in and gets an account automatically. If off, only people you invite can sign in.
                          </div>
                        </div>
                      </label>
                      {jitEnabled && (
                        <div className="mt-2 ml-7">
                          <label className="text-xs text-slate-400 mb-1 block">Default role for auto-created users</label>
                          <select
                            value={jitRole}
                            onChange={(e) => setJitRole(e.target.value)}
                            className="px-3 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-white text-sm focus:outline-none focus:border-blue-500"
                          >
                            {JIT_ROLE_OPTIONS.map((r) => (<option key={r.value} value={r.value}>{r.label}</option>))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Section>

              {/* Section 4: First Admin */}
              <Section title="First Org Superadmin" number="4">
                <p className="text-xs text-slate-500 mb-3">
                  This is the customer&apos;s primary admin. They&apos;ll be able to add the rest of their team after they sign in.
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Full Name *">
                    <input
                      value={adminName}
                      onChange={e => setAdminName(e.target.value)}
                      placeholder="Sarah Chen"
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none transition"
                    />
                  </Field>
                  <Field label="Email *">
                    <input
                      type="email"
                      value={adminEmail}
                      onChange={e => setAdminEmail(e.target.value)}
                      placeholder={signInMode === 'local' ? 'sarah@acme.com' : 'sarah@clientco.com'}
                      className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none transition"
                    />
                  </Field>
                </div>

                {/* PCG-managed: invite vs initial password toggle.
                    Federated/Hybrid: no toggle — the Microsoft sign-in email is sent automatically. */}
                {signInMode === 'local' ? (
                  <div className="mt-3">
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-300">
                      <input
                        type="checkbox"
                        checked={sendInvite}
                        onChange={() => setSendInvite(v => !v)}
                        className="rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500"
                      />
                      Send welcome email with set-password link
                    </label>
                    {!sendInvite && (
                      <Field label="Initial Password" extraClass="mt-3">
                        <input
                          type="password"
                          value={initialPassword}
                          onChange={e => setInitialPassword(e.target.value)}
                          placeholder="At least 8 characters"
                          className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2.5 text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none transition"
                        />
                      </Field>
                    )}
                  </div>
                ) : (
                  <div className="mt-3 rounded-lg border border-blue-500/30 bg-blue-500/5 px-4 py-3 text-xs text-blue-200">
                    A branded welcome email will be sent to <strong className="text-white">{adminEmail || 'the super user'}</strong> with a one-click <strong className="text-white">Sign in with Microsoft</strong> button. No password setup, no second email.
                  </div>
                )}
              </Section>

              {error && (
                <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm rounded-lg px-4 py-3">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 1 && (
          <div className="px-6 py-4 border-t border-slate-700/50 flex items-center justify-between flex-shrink-0">
            <button onClick={onClose} className="px-4 py-2 text-slate-400 hover:text-white text-sm transition">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!orgName || !adminName || !adminEmail}
              className="px-5 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg font-medium text-sm text-white disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-blue-500/25 transition"
            >
              Provision Organization
            </button>
          </div>
        )}
        {step === 4 && (
          <div className="px-6 py-4 border-t border-slate-700/50 flex items-center justify-end flex-shrink-0">
            <button
              onClick={finishWizard}
              className="px-5 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg font-medium text-sm text-white transition"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, number, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-6 h-6 rounded-full bg-blue-500/20 border border-blue-500/40 text-blue-400 text-xs font-bold flex items-center justify-center">
          {number}
        </div>
        <h3 className="text-sm font-semibold text-white uppercase tracking-wide">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function Field({ label, extraClass = '', children }) {
  return (
    <div className={extraClass}>
      <label className="block text-xs font-medium text-slate-400 uppercase tracking-wide mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function AppGroup({ label, children }) {
  return (
    <div className="mb-4">
      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">{label}</p>
      <div className="grid grid-cols-2 gap-2">{children}</div>
    </div>
  );
}

function AppCheckbox({ app, checked, onToggle }) {
  return (
    <label className={`flex items-start gap-2 p-2.5 rounded-lg border cursor-pointer transition ${
      checked ? 'bg-blue-500/10 border-blue-500/40' : 'bg-slate-800/40 border-slate-700/60 hover:border-slate-600'
    }`}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={onToggle}
        className="mt-0.5 rounded border-slate-600 bg-slate-800 text-blue-500 focus:ring-blue-500 flex-shrink-0"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium text-white truncate">{app.display_name}</p>
          {app.is_compliance_only && (
            <span className="text-[9px] bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded uppercase font-bold tracking-wide">Compliance Only</span>
          )}
        </div>
        {app.description && <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{app.description}</p>}
      </div>
    </label>
  );
}
